import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/Pagination";
import {
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  CategoryDto,
} from "@/store/apiSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash2 } from "lucide-react";

/** Must stay within backend `CategoryPageNumberPagination.max_page_size` (100). */
const CATEGORY_PAGE_SIZE = 12;

function formatCount(singular: string, count: number): string {
  if (count === 1) return `1 ${singular}`;
  if (singular.endsWith("y") && !"aeiou".includes(singular.charAt(singular.length - 2))) {
    return `${count} ${singular.slice(0, -1)}ies`;
  }
  return `${count} ${singular}s`;
}

/** Why deletion is blocked, matching the API message when counts are already known. */
function categoryInUseMessage(category: CategoryDto): string | null {
  const scenarios = category.exercise_count ?? 0;
  const children = category.child_count ?? 0;
  const parts: string[] = [];
  if (scenarios) parts.push(formatCount("scenario", scenarios));
  if (children) parts.push(formatCount("child category", children));
  if (parts.length === 0) return null;
  const joined = parts.length === 1 ? parts[0] : `${parts[0]} and ${parts[1]}`;
  const verb = scenarios + children === 1 ? "uses" : "use";
  return `This category can't be deleted because ${joined} still ${verb} it.`;
}

function readApiError(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (data && typeof data === "object" && data !== null && "detail" in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail.trim()) return detail;
      if (Array.isArray(detail)) {
        const text = detail.filter((item) => typeof item === "string").join(" ");
        if (text) return text;
      }
    }
  }
  return fallback;
}

export default function CategoriesPage() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError } = useListCategoriesQuery({
    status: statusFilter === "all" ? undefined : statusFilter,
    page,
    page_size: CATEGORY_PAGE_SIZE,
  });
  const categoryList = data?.results ?? [];
  const totalCount = data?.count ?? categoryList.length;
  const totalPages = Math.max(1, data?.total_pages ?? 1);
  const currentPage = data?.current_page ?? page;
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * CATEGORY_PAGE_SIZE + 1;
  const rangeEnd = totalCount === 0 ? 0 : rangeStart + categoryList.length - 1;

  useEffect(() => {
    if (isError && page > 1) {
      setPage(1);
      return;
    }
    if (!data) return;
    const pages = Math.max(1, data.total_pages ?? 1);
    if (page > pages) setPage(pages);
  }, [data, isError, page]);

  const setStatus = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryDto | null>(null);
  const [deleting, setDeleting] = useState<CategoryDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; api?: string }>({});
  const [form, setForm] = useState<{
    name: string;
    slug: string;
    is_published: boolean;
    is_featured: boolean;
    is_disabled: boolean;
    order: string;
  }>({
    name: "",
    slug: "",
    is_published: true,
    is_featured: false,
    is_disabled: false,
    order: "0",
  });

  const openCreateModal = () => {
    setEditing(null);
    setForm({
      name: "",
      slug: "",
      is_published: true,
      is_featured: false,
      is_disabled: false,
      order: "0",
    });
    setIsModalOpen(true);
  };

  const openDeleteDialog = (category: CategoryDto) => {
    setDeleteError(null);
    setDeleting(category);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setDeleting(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const blocked = categoryInUseMessage(deleting);
    if (blocked) {
      setDeleteError(blocked);
      return;
    }
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCategory(deleting.id).unwrap();
      setDeleting(null);
    } catch (error: unknown) {
      setDeleteError(readApiError(error, "Couldn't delete this category."));
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = (category: CategoryDto) => {
    setEditing(category);
    setForm({
      name: category.name,
      slug: category.slug || "",
      is_published: category.is_published,
      is_featured: category.is_featured,
      is_disabled: category.is_disabled,
      order: String(category.order ?? 0),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setErrors({});

    const trimmedName = form.name.trim();
    const trimmedSlug = form.slug.trim();
    const orderValue = Number(form.order || 0);

    if (!trimmedName) {
      setErrors({ name: "Name is required." });
      return;
    }

    const payload = {
      name: trimmedName,
      slug: trimmedSlug || null,
      is_published: form.is_published,
      is_featured: form.is_featured,
      is_disabled: form.is_disabled,
      order: orderValue,
    };

    try {
      if (editing) {
        await updateCategory({ id: editing.id, ...payload }).unwrap();
      } else {
        await createCategory(payload).unwrap();
        setPage(1);
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Failed to save category:", error);
      const apiData = error?.data;
      if (apiData && typeof apiData === "object") {
        const firstKey = Object.keys(apiData)[0];
        const value = (apiData as any)[firstKey];
        if (Array.isArray(value)) {
          setErrors((prev) => ({ ...prev, api: value.join(" ") }));
          return;
        }
        if (typeof value === "string") {
          setErrors((prev) => ({ ...prev, api: value }));
          return;
        }
      }
      setErrors((prev) => ({
        ...prev,
        api: "Something went wrong while saving the category.",
      }));
    }
  };

  const deleteBlocked = deleting ? deleteError || categoryInUseMessage(deleting) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground">
            Manage logical content categories and their visibility.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-md border px-1 py-0.5 bg-background">
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "all" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatus("all")}
            >
              All
            </Button>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "published" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatus("published")}
            >
              Published
            </Button>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "unpublished" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatus("unpublished")}
            >
              Unpublished
            </Button>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "disabled" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatus("disabled")}
            >
              Disabled
            </Button>
          </div>

          <Button type="button" onClick={openCreateModal}>
            + Create Category
          </Button>
        </div>
      </div>

      <div className="border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className={isFetching && !isLoading ? "opacity-60" : undefined}>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                Loading categories…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && categoryList.map((c: CategoryDto) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.slug || "-"}</TableCell>
                <TableCell>
                  <div className="flex flex-col text-xs">
                    <span>{c.is_published ? "Published" : "Unpublished"}</span>
                    <span>{c.is_featured ? "Featured" : "Not featured"}</span>
                    <span>{c.is_disabled ? "Disabled" : "Active"}</span>
                  </div>
                </TableCell>
                <TableCell>{c.order}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => openEditModal(c)}
                      aria-label={`Edit ${c.name}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => openDeleteDialog(c)}
                      aria-label={`Delete ${c.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                  Couldn't load categories.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && categoryList.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                  No categories found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex flex-col gap-2 border-t px-2 py-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="px-2 text-sm text-muted-foreground">
            {isLoading
              ? "Loading categories…"
              : isError
                ? "Couldn't load categories."
                : totalCount === 0
                  ? "No categories"
                  : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`}
          </p>
          {totalPages > 1 && (
            <Pagination
              className="w-full gap-4 p-2 sm:w-auto"
              totalPages={totalPages}
              currentPage={currentPage}
              onFirst={() => setPage(1)}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              onLast={() => setPage(totalPages)}
            />
          )}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "Create category"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Category name"
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Slug (optional)</label>
              <Input
                value={form.slug}
                onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="auto-generated from name if left blank"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="flex items-center justify-between text-sm font-medium">
                  <span>Published</span>
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(checked: boolean) =>
                      setForm((prev) => ({ ...prev, is_published: checked }))
                    }
                  />
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center justify-between text-sm font-medium">
                  <span>Featured</span>
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(checked: boolean) =>
                      setForm((prev) => ({ ...prev, is_featured: checked }))
                    }
                  />
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center justify-between text-sm font-medium">
                  <span>Disabled</span>
                  <Switch
                    checked={form.is_disabled}
                    onCheckedChange={(checked: boolean) =>
                      setForm((prev) => ({ ...prev, is_disabled: checked }))
                    }
                  />
                </label>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Order</label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm((prev) => ({ ...prev, order: e.target.value }))}
              />
            </div>

            {errors.api && (
              <p className="text-xs text-red-500 mt-1">{errors.api}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit}>
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {deleteBlocked ? "Can't delete category" : "Delete category?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deleteBlocked
              ? deleteBlocked
              : `Delete "${deleting?.name}"? This cannot be undone.`}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDeleteDialog} disabled={isDeleting}>
              {deleteBlocked ? "Close" : "Cancel"}
            </Button>
            {!deleteBlocked && (
              <Button type="button" variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting…" : "Delete"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
